import React, { useMemo } from "react";
import { sanitizeDescriptionHtml, truncateHtmlByWords, validateDescriptionLineHeight } from "./descriptionSecurity";
import "./RichDescription.css";

function normalizePlainText(text) {
  return typeof text === "string" ? text.replace(/\\n/g, "\n") : "";
}

function truncatePlainText(text, maxWords) {
  if (!text || !Number.isInteger(maxWords) || maxWords <= 0) return { text, isTruncated: false };
  const matches = [...text.matchAll(/\S+/g)];
  if (matches.length <= maxWords) return { text, isTruncated: false };
  const lastWord = matches[maxWords - 1];
  return { text: `${text.slice(0, lastWord.index + lastWord[0].length).trimEnd()}…`, isTruncated: true };
}

export default function RichDescription({
  descriptionHtml,
  description,
  descriptionLineHeight,
  className = "",
  maxWords,
  expanded = true,
  onToggle,
  toggleClassName = "",
  onToggleClick,
}) {
  const sanitizedHtml = useMemo(() => sanitizeDescriptionHtml(descriptionHtml), [descriptionHtml]);
  const hasRichDescription = Boolean(sanitizedHtml && /\S/.test(sanitizedHtml));
  const plainText = normalizePlainText(description);
  const safeLineHeight = validateDescriptionLineHeight(descriptionLineHeight);
  const richResult = useMemo(() => truncateHtmlByWords(sanitizedHtml, maxWords), [sanitizedHtml, maxWords]);
  const plainResult = useMemo(() => truncatePlainText(plainText, maxWords), [plainText, maxWords]);
  const isTruncated = hasRichDescription ? richResult.isTruncated : plainResult.isTruncated;
  const displayHtml = expanded ? sanitizedHtml : richResult.html;
  const displayText = expanded ? plainText : plainResult.text;

  if (!hasRichDescription && !plainText) return null;

  const handleToggle = (event) => {
    onToggleClick?.(event);
    onToggle?.(!expanded);
  };

  return (
    <div
      className={`rich-description min-w-0 ${className}`.trim()}
      data-testid="rich-description"
      style={{
        overflowWrap: "anywhere",
        wordBreak: "normal",
        whiteSpace: "normal",
        ...(safeLineHeight ? { lineHeight: safeLineHeight } : {}),
      }}
    >
      {hasRichDescription ? (
        <div className="rich-description__content" dangerouslySetInnerHTML={{ __html: displayHtml }} />
      ) : (
        <span className="rich-description__content" style={{ whiteSpace: "pre-wrap" }}>{displayText}</span>
      )}
      {isTruncated && onToggle && (
        <button type="button" onClick={handleToggle} className={toggleClassName}>
          {expanded ? "See less" : "See more"}
        </button>
      )}
    </div>
  );
}
